import { useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Paper, Stack, Table, TableBody,
  TableCell, TableFooter, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { reportsApi } from "@/api/endpoints";
import { errorMessage } from "@/api/client";

const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" });

/**
 * UC-M1-REP-03 — PO status report. Counts open / partial / closed POs per vendor
 * for the chosen date range, with the total value summed. Drives the procurement
 * dashboards and vendor reviews.
 */
export function PoStatusReportPage() {
  const navigate = useNavigate();
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const query = useQuery({
    queryKey: ["report-po-status", fromDate, toDate],
    queryFn: () => reportsApi.poStatus({
      fromDate: fromDate || undefined, toDate: toDate || undefined,
    }),
  });

  const rows = query.data ?? [];
  const totals = rows.reduce((acc, r) => ({
    draft: acc.draft + r.draft, submitted: acc.submitted + r.submitted,
    approved: acc.approved + r.approved, posted: acc.posted + r.posted,
    cancelled: acc.cancelled + r.cancelled, totalValue: acc.totalValue + r.totalValue,
  }), { draft: 0, submitted: 0, approved: 0, posted: 0, cancelled: 0, totalValue: 0 });

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>Purchase order status</Typography>
        <Button onClick={() => navigate("/reports")}>Back to reports</Button>
      </Stack>
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField type="date" label="From" size="small" InputLabelProps={{ shrink: true }}
            value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          <TextField type="date" label="To" size="small" InputLabelProps={{ shrink: true }}
            value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </Stack>
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        {query.isLoading
          ? <CircularProgress />
          : rows.length === 0
            ? <Alert severity="info">No purchase orders found in the selected range.</Alert>
            : (
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell>Vendor</TableCell>
                  <TableCell align="right">Draft</TableCell>
                  <TableCell align="right">Submitted</TableCell>
                  <TableCell align="right">Approved</TableCell>
                  <TableCell align="right">Posted</TableCell>
                  <TableCell align="right">Cancelled</TableCell>
                  <TableCell align="right">Total value</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {rows.map(r => (
                    <TableRow key={r.vendorId} hover>
                      <TableCell>{r.vendorName}</TableCell>
                      <TableCell align="right">{r.draft || ""}</TableCell>
                      <TableCell align="right">{r.submitted
                        ? <Chip size="small" variant="outlined" color="warning" label={r.submitted} /> : ""}</TableCell>
                      <TableCell align="right">{r.approved
                        ? <Chip size="small" variant="outlined" color="info" label={r.approved} /> : ""}</TableCell>
                      <TableCell align="right">{r.posted
                        ? <Chip size="small" variant="outlined" color="success" label={r.posted} /> : ""}</TableCell>
                      <TableCell align="right">{r.cancelled || ""}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 500 }}>{inr.format(r.totalValue)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Totals</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{totals.draft}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{totals.submitted}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{totals.approved}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{totals.posted}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{totals.cancelled}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{inr.format(totals.totalValue)}</TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            )}
      </Paper>
    </Box>
  );
}
